/**
 * Shared utility functions for GLACC-Web
 */

/**
 * Strip accents / diacritics from a string.
 * e.g. "café" → "cafe", "naïve" → "naive"
 */
export function stripAccents(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Encode email for use as a Firebase Realtime Database key.
 * Replaces characters not allowed in database keys.
 */
export function encodeEmail(email) {
    return email.replace(/[.#$[\]]/g, '_');
}

/**
 * Sanitize a string for use as a Firebase Realtime Database key.
 */
export function sanitizeKey(key) {
    return key.replace(/[.#$/[\]]/g, '_');
}

/**
 * Excel-style wildcard matching.
 *   * matches any sequence of characters (including zero)
 *   ? matches a single character
 *   ~ escapes *, ?, or ~ to match them literally
 */
export function matchesExcelWildcard(text, pattern) {
    const lowerText = stripAccents(text.toLowerCase());
    const lowerPattern = stripAccents(pattern.toLowerCase());

    // If pattern doesn't contain wildcards, do simple exact match / includes check
    if (!lowerPattern.includes('*') && !lowerPattern.includes('?') && !lowerPattern.includes('~')) {
        return lowerText === lowerPattern || lowerText.includes(lowerPattern);
    }

    // Convert Excel wildcard pattern to regex
    let regexPattern = '';
    let i = 0;
    while (i < lowerPattern.length) {
        if (lowerPattern[i] === '~') {
            if (i + 1 < lowerPattern.length) {
                const nextChar = lowerPattern[i + 1];
                regexPattern += nextChar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                i += 2;
            } else {
                regexPattern += '~';
                i++;
            }
        } else if (lowerPattern[i] === '*') {
            regexPattern += '.*';
            i++;
        } else if (lowerPattern[i] === '?') {
            regexPattern += '.';
            i++;
        } else {
            regexPattern += lowerPattern[i].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            i++;
        }
    }

    try {
        const regex = new RegExp(regexPattern);
        return regex.test(lowerText);
    } catch (e) {
        return lowerText.includes(lowerPattern.replace(/[~*?]/g, ''));
    }
}

/**
 * Classify a description against a mapping dictionary.
 * @param {string} description - The description to classify.
 * @param {Object} mappings - Object of { lowerCaseKey: glAccount }.
 * @returns {string} The matched GL account or 'No Match'.
 */
export function classifyDescription(description, mappings) {
    const lowerDesc = stripAccents(description.toLowerCase());

    // Exact match (case-insensitive, accent-insensitive)
    if (mappings[lowerDesc]) {
        return mappings[lowerDesc];
    }

    // Also try matching against accent-stripped keys
    for (const key of Object.keys(mappings)) {
        if (stripAccents(key) === lowerDesc) {
            return mappings[key];
        }
    }

    // Find matching keywords with Excel wildcard support
    const matchingKeywords = Object.keys(mappings).filter(key => {
        if (key.trim().length < 5) return false;

        const hasWildcards = key.includes('*') || key.includes('?') || key.includes('~');
        if (hasWildcards) {
            return matchesExcelWildcard(description, key);
        }
        return lowerDesc.includes(stripAccents(key));
    });

    if (matchingKeywords.length === 0) {
        return 'No Match';
    }

    // Prioritise: exact-length > literal over wildcard > longest > alphabetical
    const sortedKeywords = matchingKeywords.sort((a, b) => {
        const aExact = lowerDesc === a.toLowerCase();
        const bExact = lowerDesc === b.toLowerCase();
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        const aWild = a.includes('*') || a.includes('?') || a.includes('~');
        const bWild = b.includes('*') || b.includes('?') || b.includes('~');
        if (!aWild && bWild) return -1;
        if (aWild && !bWild) return 1;

        if (a.length !== b.length) return b.length - a.length;
        return a.localeCompare(b);
    });

    return mappings[sortedKeywords[0]];
}
